import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Table, PageHeader, Card, Select, Button, showSnackbar, QuickFilterTypes } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import PopoverList from "components/popover/PopoverList";
import Helmet from "components/helmet/Helmet";

import { DataFormat, Language, usePermission, Fields, hasPluginPermission, hasOptionalFieldPermission } from "imports/Utils";

import { FleetLink, RelationLink } from "utils/LinkUtils";

import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";

import { getAvailabilityHoursPerDay } from "view/ships/utils/ShipUtils";
import UploadShipsDialog from "view/ships/dialogs/UploadShipsDialog";
import ShipBargeTypeSelect from "view/ships/components/selects/ShipBargeTypeSelect";
import ShipFuelTypeSelect from "view/ships/components/selects/ShipFuelTypeSelect";
import ShipVesselTypeSelect from "view/ships/components/selects/ShipVesselTypeSelect";
import ShipCemtClassificationTypeSelect, { ShipCemtClassificationTypeSelectOption } from "view/ships/components/selects/ShipCemtClassificationTypeSelect";

import { ChildPropsFromPagination } from "types/commons";
import { SortDirection, RelationType } from "__generated__/enums";
import { GetShipsDocument, GetShipsQuery } from "__generated__/view/ships/graphql/ship.queries.generated";
import { DeleteShipDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";

type ShipFromQuery = ChildPropsFromPagination<GetShipsQuery, "ships">;

const Ships = () => {
	const client = useApolloClient();

	const confirm = useConfirm();
	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const [ dialogOpen, openDialog ] = useState(false);

	const { isRTR, isMasterdata, isReadOnly } = usePermission();
	const canEditOrDelete = !isReadOnly && (isRTR || isMasterdata);

	const { tableProps, refetchAfterDelete, refetch } = useBackendTable({
		id: TableId.Ships,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetShipsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							level: 3,
							name: filter.name,
							externalId: filter.externalId,
							eni: filter.eni,
							mmsi: filter.mmsi,
							yearOfBuild: filter.yearOfBuild,
							yearLastMaintenance: filter.yearLastMaintenance,
							bobbyCat: filter.bobbyCat,
							dropline: filter.dropline,
							hasShutters: filter.hasShutters,
							shuttersType: filter.shuttersType,
							numberOfCranes: filter.numberOfCranes,
							maxCranesOnQuay: filter.maxCranesOnQuay,
							corridor: filter.corridor,
							tankEye: filter.tankEye,
							hasHeatingInstallation: filter.hasHeatingInstallation,
							availableHoursADay: filter.availableHoursADay,
							availabilityProperties: {
								capacityTeu: filter.capacityTeu || undefined,
								capacityTon: filter.capacityTon || undefined,
							},
							avgVelocityEmptyKmH: filter.avgVelocityEmptyKmH,
							avgVelocityFullKmH: filter.avgVelocityFullKmH,
							deckLoad: filter.deckLoad,
							avgSpeedUpstreamKmH: filter.avgSpeedUpstreamKmH,
							avgSpeedDownstreamKmH: filter.avgSpeedDownstreamKmH,
							maxTonBallast: filter.maxTonBallast,
							airDraft: filter.airDraft,
							lengthCm: filter.lengthCm ? [ filter.lengthCm * 100, filter.lengthCm * 100 ] : undefined,
							widthCm: filter.widthCm ? [ filter.widthCm * 100, filter.widthCm * 100 ] : undefined,
							minDraughtCm: filter.minDraughtCm ? [ filter.minDraughtCm * 100, filter.minDraughtCm * 100 ] : undefined,
							maxDraughtCm: filter.maxDraughtCm ? [ filter.maxDraughtCm * 100, filter.maxDraughtCm * 100 ] : undefined,
							cemtClassificationTypeIds: filter.cemtClassificationType?.map((option: ShipCemtClassificationTypeSelectOption) => option.id),
							operators: filter.operator?.map((option: RelationSelectOption) => option.id),
							vesselType: filter.vesselType,
							owners: filter.owner?.map((option: RelationSelectOption) => option.id),
							skippers: filter.skipper?.map((option: RelationSelectOption) => option.id),
							fleets: filter.fleets?.map((option: FleetSelectOption) => option.id),
							bargeType: filter.bargeType,
							fuelType: filter.fuelType,
						},
					},
				});

				callback({
					content: result.data.ships.content,
					totalResults: result.data.ships.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ removeShip ] = useMutation(DeleteShipDocument);

	const deleteShip = (id: number) => {
		removeShip({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("SHIPS")}
					<Helmet title={intl.formatMessage({ id: "SHIPS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{isRTR && !isReadOnly && (
						<Button onClick={() => openDialog(true)} variant="outlined">
							{intl.formatMessage({ id: "UPLOAD_USING_EXCEL" })}
						</Button>
					)}

					{isRTR && !isReadOnly &&
						<Button onClick={() => navigate("/view/ships/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row) => navigate(`/view/ships/${row.id}/details`)}
					columns={[ {
						id: "name",
						title: intl.formatMessage({ id: "SHIPS_NAME" }),
						sortable: true,
						width: 200,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "externalId",
						title: intl.formatMessage({ id: "EXTERNAL_ID" }),
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "externalId"),
					}, {
						id: "operator",
						title: intl.formatMessage({ id: "OPERATOR" }),
						sortable: true,
						width: isRTR ? 200 : undefined,
						render: (row: ShipFromQuery) => <RelationLink relation={row.operator} />,
						filterComponent: isRTR ? (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "OPERATOR" })}
								value={value}
								onChange={(options) => onChange(options)}
								types={[ RelationType.Operator ]}
								multiple />
						) : undefined,
					}, {
						id: "eni",
						title: intl.formatMessage({ id: "SHIP_ADD_ENI" }),
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "eni"),
					}, {
						id: "mmsi",
						title: intl.formatMessage({ id: "SHIP_ADD_MMSI" }),
						sortable: true,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "mmsi"),
					}, {
						id: "vesselType",
						title: intl.formatMessage({ id: "SHIP_ADD_VESSEL_TYPE" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.vesselType && intl.formatMessage({ id: row.vesselType }),
						filterComponent: (value, onChange) => (
							<ShipVesselTypeSelect
								label={intl.formatMessage({ id: "SHIP_ADD_VESSEL_TYPE" })}
								value={value}
								onChange={(options) => onChange(options)} />
						),
					}, {
						id: "cemtClassificationType",
						title: intl.formatMessage({ id: "SHIP_CEMT_CLASSIFICATION_TYPE" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.cemtClassificationType?.canonicalName,
						width: 200,
						filterComponent: (value, onChange) => (
							<ShipCemtClassificationTypeSelect
								label={intl.formatMessage({ id: "SHIP_CEMT_CLASSIFICATION_TYPE" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "cemtClassificationType"),
					}, {
						id: "bargeType",
						title: intl.formatMessage({ id: "BARGE_TYPE" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.bargeType && intl.formatMessage({ id: `BARGE_TYPE_${row.bargeType}` }),
						width: 200,
						defaultVisible: false,
						filterComponent: (value, onChange) => (
							<ShipBargeTypeSelect
								label={intl.formatMessage({ id: "BARGE_TYPE" })}
								value={value}
								onChange={(ids) => onChange(ids)}
								multiple />
						),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "bargeType"),
					}, {
						id: "yearOfBuild",
						title: intl.formatMessage({ id: "SHIPS_YEAR_OF_BUILD" }),
						sortable: true,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "yearOfBuild"),
					}, {
						id: "yearLastMaintenance",
						title: intl.formatMessage({ id: "SHIPS_YEAR_LAST_MAINTENANCE" }),
						sortable: true,
						width: 170,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "yearLastMaintenance"),
					}, {
						id: "lengthCm",
						title: intl.formatMessage({ id: "SHIPS_LENGTH" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.lengthCm && DataFormat.simpleUnit(row.lengthCm / 100, DataFormat.UNITS.METER),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "dimensions"),
					}, {
						id: "widthCm",
						title: intl.formatMessage({ id: "SHIPS_WIDTH" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.widthCm && DataFormat.simpleUnit(row.widthCm / 100, DataFormat.UNITS.METER),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "dimensions"),
					}, {
						id: "minDraughtCm",
						title: intl.formatMessage({ id: "SHIPS_MIN_DRAUGHT" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.minDraughtCm && DataFormat.simpleUnit(row.minDraughtCm / 100, DataFormat.UNITS.METER),
						width: 150,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
					}, {
						id: "maxDraughtCm",
						title: intl.formatMessage({ id: "SHIPS_MAX_DRAUGHT" }),
						sortable: true,
						render: (row: ShipFromQuery) => row.maxDraughtCm && DataFormat.simpleUnit(row.maxDraughtCm / 100, DataFormat.UNITS.METER),
						width: 150,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
					}, {
						id: "fuelType",
						title: intl.formatMessage({ id: "SHIPS_FUEL_TYPE" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => row.fuelType && intl.formatMessage({ id: `SHIP_FUEL_${row.fuelType}` }),
						defaultVisible: false,
						filterComponent: (value, onChange) => (
							<ShipFuelTypeSelect
								label={intl.formatMessage({ id: "SHIPS_FUEL_TYPE" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "fuelType"),
					}, {
						id: "avgVelocityEmptyKmH",
						title: intl.formatMessage({ id: "SHIPS_AVG_VELOCITY_EMPTY" }),
						sortable: true,
						width: 220,
						render: (row: ShipFromQuery) => row.avgVelocityEmptyKmH && DataFormat.simpleUnit(row.avgVelocityEmptyKmH, DataFormat.UNITS.KMH),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityEmptyKmH"),
					}, {
						id: "avgVelocityFullKmH",
						title: intl.formatMessage({ id: "SHIPS_AVG_VELOCITY_FULL" }),
						sortable: true,
						width: 220,
						render: (row: ShipFromQuery) => row.avgVelocityFullKmH && DataFormat.simpleUnit(row.avgVelocityFullKmH, DataFormat.UNITS.KMH),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityFullKmH"),
					}, {
						id: "bobbyCat",
						title: intl.formatMessage({ id: "SHIPS_BOBBY_CAT" }),
						sortable: true,
						width: 130,
						render: (row: ShipFromQuery) => DataFormat.boolean(row.bobbyCat),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.BOOLEAN,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "bobbyCat"),
					}, {
						id: "dropline",
						title: intl.formatMessage({ id: "SHIPS_DROPLINE" }),
						sortable: true,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "dropline"),
					}, {
						id: "capacityTeu",
						title: intl.formatMessage({ id: "CAPACITY_TEU" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => row.capacityTeu && DataFormat.simpleUnit(row.capacityTeu, DataFormat.UNITS.TEU),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "capacityTeu"),
					}, {
						id: "capacityTon",
						title: intl.formatMessage({ id: "CAPACITY_TON" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => row.capacityTon && DataFormat.simpleUnit(row.capacityTon, DataFormat.UNITS.MTON),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "capacityTon"),
					}, {
						id: "hasShutters",
						title: intl.formatMessage({ id: "SHIP_HAS_SHUTTERS" }),
						sortable: true,
						width: 130,
						render: (row: ShipFromQuery) => DataFormat.boolean(row.hasShutters),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.BOOLEAN,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "hasShutters"),
					}, {
						id: "shuttersType",
						title: intl.formatMessage({ id: "SHIP_SHUTTERS_TYPE" }),
						sortable: true,
						width: 130,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.TEXT,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "shuttersType"),
					}, {
						id: "deckLoad",
						title: intl.formatMessage({ id: "SHIP_DECK_LOAD" }),
						sortable: true,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "deckLoad"),
					}, {
						id: "avgSpeedUpstreamKmH",
						title: intl.formatMessage({ id: "SHIP_AVG_SPEED_UPSTREAM_KMH" }),
						sortable: true,
						width: 300,
						render: (row: ShipFromQuery) => row.avgSpeedUpstreamKmH && DataFormat.simpleUnit(row.avgSpeedUpstreamKmH, DataFormat.UNITS.KMH),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedUpstreamKmH"),
					}, {
						id: "avgSpeedDownstreamKmH",
						title: intl.formatMessage({ id: "SHIP_AVG_SPEED_DOWNSTREAM_KMH" }),
						sortable: true,
						width: 300,
						render: (row: ShipFromQuery) => row.avgSpeedDownstreamKmH && DataFormat.simpleUnit(row.avgSpeedDownstreamKmH, DataFormat.UNITS.KMH),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedDownstreamKmH"),
					}, {
						id: "numberOfCranes",
						title: intl.formatMessage({ id: "SHIP_NUMBER_OF_CRANES" }),
						sortable: true,
						width: 140,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "numberOfCranes"),
					}, {
						id: "maxCranesOnQuay",
						title: intl.formatMessage({ id: "SHIP_MAX_CRANES_ON_QUAY" }),
						sortable: true,
						width: 180,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "maxCranesOnQuays"),
					}, {
						id: "maxTonBallast",
						title: intl.formatMessage({ id: "SHIPS_MAX_TON_BALLAST" }),
						sortable: true,
						width: 150,
						render: (row: ShipFromQuery) => row.maxTonBallast && DataFormat.simpleUnit(row.maxTonBallast, DataFormat.UNITS.MTON),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "maxTonBallast"),
					}, {
						id: "airDraft",
						title: intl.formatMessage({ id: "SHIPS_AIR_DRAIFT" }),
						sortable: true,
						width: 130,
						render: (row: ShipFromQuery) => row.airDraft && DataFormat.simpleUnit(row.airDraft, DataFormat.UNITS.METER),
						defaultVisible: false,
						filterComponent: QuickFilterTypes.NUMBER,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "airDraft"),
					}, {
						id: "corridor",
						title: intl.formatMessage({ id: "SHIPS_CORRIDOR" }),
						sortable: true,
						width: 130,
						defaultVisible: false,
						filterComponent: QuickFilterTypes.TEXT,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "corridor"),
					}, {
						id: "tankEye",
						title: intl.formatMessage({ id: "TANK_EYE" }),
						defaultVisible: false,
						sortable: true,
						filterComponent: QuickFilterTypes.BOOLEAN,
						render: (row: ShipFromQuery) => DataFormat.boolean(row.tankEye),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "tankEye"),
					}, {
						id: "hasHeatingInstallation",
						title: intl.formatMessage({ id: "HAS_HEATING_INSTALLATION" }),
						defaultVisible: false,
						sortable: true,
						filterComponent: QuickFilterTypes.BOOLEAN,
						render: (row: ShipFromQuery) => DataFormat.boolean(row.hasHeatingInstallation),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "hasHeatingInstallation"),
					}, {
						id: "availableHoursADay",
						title: intl.formatMessage({ id: "SHIP_AVAILABLE_HOURS_A_DAY" }),
						defaultVisible: false,
						sortable: true,
						sortKey: "availability.availableHoursADay",
						render: (row: ShipFromQuery) => row.availability?.availableHoursADay,
						filterComponent: (value, onChange) => (
							<Select
								label={intl.formatMessage({ id: "SHIP_AVAILABLE_HOURS_A_DAY" })}
								value={value}
								fetchOptions={getAvailabilityHoursPerDay(session.settings)}
								onChange={onChange}
								disableSearch />
						),
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "hasHeatingInstallation"),
					}, {
						id: "owner",
						title: intl.formatMessage({ id: "SHIP_OWNER" }),
						sortable: true,
						width: isRTR ? 200 : undefined,
						filterComponent: isRTR ? (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "SHIP_OWNER" })}
								value={value}
								onChange={(options) => onChange(options)}
								types={[ RelationType.ShipManager, RelationType.Skipper ]}
								multiple
							/>
						) : undefined,
						render: (row: ShipFromQuery) => <RelationLink relation={row.owner} />,
					}, {
						id: "defaultCharterSubtype",
						title: intl.formatMessage({ id: "CHARTER_TYPE" }),
						defaultVisible: false,
						filterComponent: isRTR ? (value, onChange) => (
							<CharterSubtypeSelect
								label={intl.formatMessage({ id: "CHARTER_TYPE" })}
								value={value}
								onChange={(options) => onChange(options)}
							/>
						) : undefined,
						render: (row: ShipFromQuery) => <CharterSubtypeTag type={row.defaultCharterSubtype} />,
						disabled: !hasOptionalFieldPermission(session, Fields.SHIP, "defaultCharterType"),
					}, {
						id: "fleets",
						title: intl.formatMessage({ id: "FLEETS" }),
						width: isRTR ? 200 : undefined,
						filterComponent: isRTR ? (value, onChange) => (
							<FleetSelect
								value={value}
								onChange={(options) => onChange(options)}
								label={intl.formatMessage({ id: "FLEETS" })}
								multiple />
						) : undefined,
						render: (row: ShipFromQuery) => (
							<PopoverList
								list={row.fleets}
								label={intl.formatMessage({ id: "FLEETS" })}
								render={item => <FleetLink fleet={item} />}
								limit={1} />
						),
						disabled: !hasPluginPermission(session, "fleets"),
					} ]}
					rowActions={(canEditOrDelete) ? [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/ships/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						hidden: !hasOptionalFieldPermission(session, Fields.SHIP, "externalId"),
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteShip(row.id);
							});
						},
					} ] : undefined } />
			</Card>

			<UploadShipsDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				refetch={refetch} />
		</div>
	);
};

export default Ships;
